import { useQueryClient } from "@tanstack/react-query";
import { reaction } from "mobx";
import { observer } from "mobx-react";
import React, { useEffect, useRef } from "react";
import {
  AgentActionGroupDto,
  AgentActionGroupDtoActions,
  ScheduleAgentResultDto,
  UserDto,
  WorkflowEventName,
  WorkflowResultDtoWorkflowNameEnum,
} from "../../../api-client";
import { useApi } from "../../../api/ApiProvider";
import useStore from "../../../helpers/useStore";
import { useWorkflow, WorkflowEventDiscriminated } from "../../../hooks/useWorkflow";
import { AgentChatContext } from "../../../stores/AgentChat/agentChat.provider";
import { getTimeSlotDisplayDataBulk } from "../../../stores/Appointment/appointment.queries";
import { useUsersByLocation } from "../../../stores/Calendar/calendar.queries";
import { MutationDraftContext } from "../../../stores/MutationDraft/mutationDraft.provider";
import { fetchPatientsMinimal } from "../../../stores/Patients/patient.queries";
import {
  DrivingTimeMutation,
  FailedPlacement,
  MutationPlan,
  MutationRequest,
  PlacementMutation,
  ShiftMutation,
} from "../../../stores/MutationDraft/types";
import { useLocationId } from "../../../stores/User/User.hooks";

// ─── Helpers ─────────────────────────────────────────────────────────────────

type NameResolver = (therapistId: string) => string;

function durationMinutesFromRange(start: string, end: string): number {
  const ms = new Date(end).getTime() - new Date(start).getTime();
  return Math.round(ms / 60_000);
}

export type ReschedulingData = Pick<
  AgentActionGroupDtoActions,
  "TIME_SLOT_CREATE" | "TIME_SLOT_MOVE" | "TIME_SLOT_SKIP" | "TRAVEL_TIME_SLOT_CHANGE"
>;

export interface ActionGroupProcessingResult {
  allTimeSlotIds: Set<string>;
  totalPlaced: number;
  totalMoves: number;
  totalUnplaced: number;
}

/**
 * Parse action groups, collect all timeSlotIds, pre-fetch display data,
 * and apply one draft per action group to the store.
 *
 * Each action group becomes one MutationPlan keyed by actionGroupId.
 */
export async function processActionGroups(opts: {
  actionGroups: AgentActionGroupDto[];
  queryClient: ReturnType<typeof useQueryClient>;
  appointmentsApi: ReturnType<typeof useApi>["appointments"];
  patientsApi: ReturnType<typeof useApi>["patients"];
  applyDraft: (groupId: string, data: ReschedulingData, groupMeta: { dayStr: string }) => void;
}): Promise<ActionGroupProcessingResult> {
  const { actionGroups, queryClient, appointmentsApi, patientsApi, applyDraft } = opts;

  const allTimeSlotIds = new Set<string>();
  const patientIds = new Set<number>();
  const parsed: Array<{ groupId: string; data: ReschedulingData; groupMeta: { dayStr: string } }> =
    [];
  let totalPlaced = 0,
    totalMoves = 0,
    totalUnplaced = 0;

  console.log(`actionGroups`, actionGroups);
  // 1. Parse all groups and collect timeSlotIds BEFORE triggering re-renders
  for (const group of actionGroups) {
    const { actions } = group;
    const data: ReschedulingData = {
      TIME_SLOT_MOVE: actions.TIME_SLOT_MOVE ?? [],
      TIME_SLOT_CREATE: actions.TIME_SLOT_CREATE ?? [],
      TIME_SLOT_SKIP: actions.TIME_SLOT_SKIP ?? [],
      TRAVEL_TIME_SLOT_CHANGE: actions.TRAVEL_TIME_SLOT_CHANGE ?? [],
    };

    parsed.push({ groupId: group.actionGroupId, data, groupMeta: { dayStr: group.dayStr } });

    for (const p of data.TIME_SLOT_CREATE!) {
      if (p.timeSlotId) {
        allTimeSlotIds.add(p.timeSlotId);
      } else {
        if (!isNaN(Number(p.patientId))) patientIds.add(Number(p.patientId));
      }
    }
    for (const u of data.TIME_SLOT_SKIP!) {
      if (u.timeSlotId) {
        allTimeSlotIds.add(u.timeSlotId);
      } else {
        if (!isNaN(Number(u.patientId))) patientIds.add(Number(u.patientId));
      }
    }
    for (const m of data.TIME_SLOT_MOVE!) {
      if (m.timeSlotId) {
        allTimeSlotIds.add(m.timeSlotId);
      } else {
        if (!isNaN(Number(m.patientId))) patientIds.add(Number(m.patientId));
      }
    }

    totalPlaced += data.TIME_SLOT_CREATE!.length;
    totalMoves += data.TIME_SLOT_MOVE!.length;
    totalUnplaced += data.TIME_SLOT_SKIP!.length;
  }

  // 2. Pre-fetch and seed individual caches BEFORE applyDraft triggers re-renders
  if (allTimeSlotIds.size > 0) {
    await getTimeSlotDisplayDataBulk(queryClient, appointmentsApi, [...allTimeSlotIds]);
  }
  if (patientIds.size > 0) {
    await fetchPatientsMinimal(queryClient, patientsApi, [...patientIds]);
  }

  // 3. Now apply drafts (triggers MobX → re-renders, but cache is already warm)
  for (const { groupId, data, groupMeta } of parsed) {
    applyDraft(groupId, data, groupMeta);
  }

  return { allTimeSlotIds, totalPlaced, totalMoves, totalUnplaced };
}

/**
 * Transform one action group's raw data into a MutationPlan.
 * All actions (creates/moves/skips/driving-time) in the group belong to this plan.
 */
function actionGroupToMutationPlan(raw: ReschedulingData, resolveName: NameResolver): MutationPlan {
  const creates = raw.TIME_SLOT_CREATE ?? [];
  const moves = raw.TIME_SLOT_MOVE ?? [];
  const skipped = raw.TIME_SLOT_SKIP ?? [];
  const drivingSlotChanges = raw.TRAVEL_TIME_SLOT_CHANGE ?? [];

  const shifts: ShiftMutation[] = moves.map((m) => {
    const newStartMs = new Date(m.newStart).getTime();
    const newEndMs = new Date(m.newEnd).getTime();
    const originalStartMs = new Date(m.originalStart).getTime();
    const dur = newEndMs - newStartMs;
    const originalEnd = !isNaN(originalStartMs + dur)
      ? new Date(originalStartMs + dur).toISOString()
      : m.newEnd;

    return {
      appointmentId: m.appointmentId,
      timeSlotId: m.timeSlotId,
      label: "Verschoben",
      originalStart: m.originalStart,
      originalEnd,
      originalTherapistId: m.originalTherapistId,
      originalTherapistName: resolveName(m.originalTherapistId),
      newStart: m.newStart,
      newEnd: m.newEnd,
      newTherapistId: m.newTherapistId,
      newTherapistName: resolveName(m.newTherapistId),
      patientId: m.patientId,
    };
  });

  const placements: PlacementMutation[] = creates.map((p) => ({
    start: p.start,
    end: p.end,
    durationMinutes: durationMinutesFromRange(p.start, p.end),
    timeSlotId: p.timeSlotId,
    appointmentId: p.appointmentId,
    therapistId: p.therapistId,
    therapistName: resolveName(p.therapistId),
    patientId: p.patientId,
  }));

  const skippedMutations: FailedPlacement[] = skipped.map((u) => ({
    appointmentId: u.appointmentId,
    timeSlotId: u.timeSlotId ?? "",
    reason: u.reason ?? "Konnte nicht platziert werden",
    patientId: u.patientId,
  }));

  const drivingTimes: DrivingTimeMutation[] = drivingSlotChanges.map((d) => ({
    id: d.id,
    action: d.action,
    collectionId: d.collectionId ?? "",
    therapistId: d.therapistId,
    therapistName: resolveName(d.therapistId),
    start: d.start,
    end: d.end,
    durationMinutes: d.durationMinutes,
  }));

  return {
    shifts,
    placements,
    drivingTimes: drivingTimes.length > 0 ? drivingTimes : undefined,
    skipped: skippedMutations.length > 0 ? skippedMutations : undefined,
  };
}

// ─── Component ───────────────────────────────────────────────────────────────

type MutationSocketBridgeProps = {
  enabled?: boolean;
  skipExisting?: boolean; // Whether to skip existing drafts when applying new ones
};

/**
 * Headless component that listens for rescheduling workflow WebSocket events
 * (progress, result, errors) and updates the global mutation draft store + chat store.
 *
 * Mounted at app level so it stays alive regardless of drawer/route unmounts.
 */
const MutationSocketBridge: React.FC<MutationSocketBridgeProps> = observer(
  ({ enabled = true, skipExisting = true }) => {
    const mutationRegistry = useStore(MutationDraftContext);
    const chatRegistry = useStore(AgentChatContext);
    const chatStore = chatRegistry.getDefaultStore();
    const draftStore = mutationRegistry.getDefaultStore();
    const { appointments: appointmentsApi, patients: patientsApi } = useApi();
    const queryClient = useQueryClient();

    // Fetch users for therapist name resolution
    const locationId = useLocationId();
    const { data: allUsers = [] } = useUsersByLocation(locationId, undefined, false, !!locationId);

    // Build a name resolver from current user list
    const resolveNameRef = useRef<NameResolver>((id) => id);
    resolveNameRef.current = (therapistId: string) => {
      const user = allUsers.find((u: UserDto) => u.id === therapistId);
      return user
        ? `${user.firstName || ""} ${user.lastName || ""}`.trim() || therapistId
        : therapistId;
    };

    // Keep latest values in refs so callbacks always read fresh state
    const skipExistingRef = useRef(skipExisting);
    skipExistingRef.current = skipExisting;
    const draftStoreRef = useRef(draftStore);
    draftStoreRef.current = draftStore;
    /** Tracks whether AgentActionGroups already applied drafts (prevents onResult race) */
    // const draftsAppliedRef = useRef(false);

    // ─── Shared helper: build MutationPlan from one action group and store it ───
    const applyDraft = (
      groupId: string,
      rawData: ReschedulingData,
      groupMeta: { dayStr: string },
    ) => {
      const store = draftStoreRef.current;
      if (skipExistingRef.current && store.getDraft(groupId)) return;

      const draft = actionGroupToMutationPlan(rawData, resolveNameRef.current);
      const firstPlacement = draft.placements[0];

      const request: MutationRequest = {
        id: groupId,
        durationMinutes: firstPlacement?.durationMinutes ?? 0,
        label: firstPlacement?.therapistName
          ? `Termin · ${firstPlacement.therapistName}`
          : `Termin · ${groupId.slice(0, 8)}`,
        source: { type: "scheduler-agent", uaId: groupId },
        dayStr: groupMeta.dayStr,
      };

      store.setDraft(groupId, draft, request);
    };

    // ─── useWorkflow handles all WebSocket subscribe/listen/cleanup ──────────────
    const { subscribeToWorkflow } = useWorkflow({
      workflowName: WorkflowResultDtoWorkflowNameEnum.ReschedulingWorkflow,

      onResult: async (data) => {
        if (!enabled) return;
        console.log("Workflow completed with result:", data);

        const rawResult = data?.data as ScheduleAgentResultDto;
        if (!rawResult) return;

        // Store the raw result as-is for agent state
        draftStore.agent.setCompleted(rawResult);
        await processActionGroups({
          actionGroups: rawResult.agentActionGroups ?? [],
          queryClient,
          appointmentsApi,
          patientsApi,
          applyDraft,
        });

        chatStore.removeLoading();
      },

      onProgress: (progressDto) => {
        if (!enabled) return;
        // console.log("Workflow progress event:", progressDto);
        draftStore.agent.setProgress(progressDto.percent ?? 0);
      },

      onError: (errorMessage) => {
        if (!enabled) return;
        draftStore.agent.setError(errorMessage);
        chatStore.removeLoading();
        chatStore.add({
          role: "system",
          content: `Fehler: ${errorMessage}`,
          timestamp: new Date(),
        });
      },

      onWorkflowEvent: async (eventDto: WorkflowEventDiscriminated) => {
        if (!enabled) return;
        console.log("Workflow event:", eventDto);
        if (eventDto.eventName === WorkflowEventName.JobProgress) {
          const progressData = eventDto.data;
          if (progressData?.percent != null) {
            draftStore.agent.setProgress(progressData.percent);
            console.log("Progress update:", progressData.percent, progressData.message);
            if (progressData.message) {
              chatStore.add({
                role: "assistant",
                content: `Fortschritt: ${progressData.message} (${progressData.percent}%)`,
                timestamp: new Date(),
                isLoading: true,
              });
            }
          }
        }

        if (eventDto.eventName === WorkflowEventName.AgentActionGroups) {
          const actionGroups = eventDto.data?.actionGroups ?? [];

          // console.log("Processing ActionGroups event with groups:", actionGroups);

          const { totalPlaced, totalMoves, totalUnplaced } = await processActionGroups({
            actionGroups,
            queryClient,
            appointmentsApi,
            patientsApi,
            applyDraft,
          });
          chatStore.add({
            role: "assistant",
            content:
              `Termin verarbeitet — ${totalPlaced} Termine platziert` +
              (totalMoves > 0 ? `, ${totalMoves} Verschiebungen` : "") +
              (totalUnplaced > 0 ? `, ${totalUnplaced} nicht platziert` : "") +
              ".",
            timestamp: new Date(),
          });
        }
      },

      showErrorNotifications: false, // we handle errors ourselves via chat
    });

    // ─── MobX reaction: subscribe to workflow when activeWorkflow changes ───────
    useEffect(() => {
      const dispose = reaction(
        () => draftStore.agent.state.activeWorkflow,
        (activeWorkflow) => {
          if (!activeWorkflow || !enabled) return;
          // draftsAppliedRef.current = false;
          subscribeToWorkflow(activeWorkflow);
        },
        { fireImmediately: true },
      );
      return dispose;
    }, [draftStore, subscribeToWorkflow, enabled]);

    return null;
  },
);

export default MutationSocketBridge;
